import PropTypes from 'prop-types';
import cx from 'classnames';
import Link from 'next/link';
import {useRouter} from 'next/router';
import {getLocalizedPath, getLocaleFromRouter, LOCALES} from 'helpers/i18n';
import styles from './links.module.scss';

/**
 * Prepare path and add it to the query parameter of the link.
 * The path is modified in this way:
 * Split by '/', then remove first occurrence and replace all others with '--'.
 * For instance, a link '/features/find' will become 'features--find'.
 * Any other query parameters are omitted.
 * Root page will indicate 'homepage' as path.
 */
const preparePathQueryParam = shouldPreserveExistingPath => {
  const router = useRouter();
  let path = router.asPath;

  // Remove any query params.
  [path = ''] = path.split('?');

  const {llsd = ''} = router.query || '';

  if (path === '/') {
    path = 'homepage';
  } else if (!shouldPreserveExistingPath || llsd === '') {
    // Remove any last dash - if any.
    path = path.endsWith('/') ? path.substring(0, path.length - 1) : path;
    // Remove initial '/' existing in path.
    // Replace any other occurrences of '/' with '--'.
    path = path.slice(1).split('/').join('--');
  }

  return `?llsd=${path}`;
};

const propTypes = {
  children: PropTypes.node,
  target: PropTypes.string,
  title: PropTypes.string,
  href: PropTypes.string,
  internal: PropTypes.bool,
  variant: PropTypes.string,
  className: PropTypes.string,
  hasPathAsQueryParam: PropTypes.bool,
  shouldPreserveExistingPath: PropTypes.bool,
  onClick: PropTypes.func,
  customAttributes: PropTypes.object
};

const LinkComponent = ({
  children,
  target = '_self',
  title = null,
  href = '',
  internal = false, // This prop is used to define urls inside the workable domain. Eg id.workable.com . Set it to true when navigating in those cases.
  variant = null,
  className = null,
  hasPathAsQueryParam = false,
  shouldPreserveExistingPath = false,
  onClick = e => {},
  customAttributes = {}
}) => {
  const router = useRouter();
  const linkClass = cx(className, styles.link, {
    [styles.link]: variant === null,
    // This allows us to add a rule on its css without modifying the logic.
    [styles[`link-${variant}`]]: variant !== null
  });

  const queryParam = hasPathAsQueryParam ? preparePathQueryParam(shouldPreserveExistingPath) : '';

  // Add a const array of the button variations that have the gradient hover effect
  const hoverEffectVariants = [
    'primary',
    'primary-no-arrow',
    'primary-sm',
    'primary-sm-no-arrow',
    'secondary',
    'secondary-trans-border',
    'secondary-no-gradient',
    'secondary-no-gradient-download',
    'secondary-sm',
    'secondary-sm-no-arrow',
    'secondary-sm-trans-border'
  ];

  /**
   * Determines whether a given URL is absolute.
   *
   * An absolute URL includes:
   * - Fully qualified URLs with a scheme (e.g., http://, https://, mailto:, etc.)
   * - Protocol-relative URLs (e.g., //example.com)
   *
   * Relative paths like /about or ./page are considered non-absolute.
   *
   * @param {string} url - The URL or path to check.
   * @returns {boolean} - True if the URL is absolute, false if it's relative.
   */

  const isUrlAbsolute = url => {
    if (typeof url !== 'string') return false;

    // Protocol-relative URLs are considered absolute (e.g., //example.com)
    if (url.startsWith('//')) return true;

    try {
      const urlObj = new URL(url);
      return Boolean(urlObj);
    } catch {
      return false;
    }
  };

  /**
   * Checks if a path already has a locale prefix
   * @param {string} path - The path to check
   * @returns {boolean} - True if path already has a locale prefix
   */
  const hasLocalePrefix = path => {
    if (typeof path !== 'string') return false;
    const nonDefaultLocales = LOCALES.SUPPORTED.filter(locale => locale !== LOCALES.DEFAULT);
    return nonDefaultLocales.some(
      locale => path.startsWith(`/${locale}/`) || path === `/${locale}`
    );
  };

  /**
   * Localizes a path based on the current locale
   * Only localizes relative paths that don't already have a locale prefix
   * @param {string} path - The path to localize
   * @returns {string} - The localized path
   */
  const localizePath = path => {
    // Don't localize if:
    // - Path is empty
    // - Path is absolute (external URL)
    // - Path already has a locale prefix
    // - Path is marked as internal (other Workable domains)
    if (!path || isUrlAbsolute(path) || hasLocalePrefix(path) || internal) {
      return path;
    }

    // Only localize relative paths starting with /
    if (path.startsWith('/')) {
      const locale = getLocaleFromRouter(router);
      return getLocalizedPath(path, locale);
    }

    return path;
  };

  // Localize the href path for internal navigation
  const localizedHref = localizePath(href);

  let innerContent = children;

  if (hoverEffectVariants.includes(variant)) {
    innerContent = (
      <>
        <span className={styles.foreground}>{children}</span>
        <span className={styles['background-wrapper']}>
          <span className={styles.background} />
        </span>
      </>
    );
  }

  // For urls defined to navigate outside the app
  if (isUrlAbsolute(href) || internal) {
    return (
      <a
        href={`${href}${queryParam}`}
        title={title}
        target={target}
        onClick={onClick}
        rel="noopener noreferrer"
        className={linkClass}
        {...customAttributes}>
        {innerContent}
      </a>
    );
  }

  return (
    <Link
      href={`${localizedHref}${queryParam}`}
      title={title}
      className={linkClass}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      onClick={onClick}
      onKeyDown={e => e.key === 'Enter' && onClick(e)}
      {...customAttributes}>
      {innerContent}
    </Link>
  );
};

LinkComponent.propTypes = propTypes;

export default LinkComponent;
